import styled from '@emotion/styled';
import {
  AuthorizationStates,
  EventTypes,
  VisibilityStates,
} from '@racemap/utilities/consts/events';
import { isContestGroupEvent, isGroupEvent } from '@racemap/utilities/functions/utils';
import { Card, Col, Row, Typography } from 'antd';
import { type FC, useEffect, useMemo } from 'react';
import { QRCode } from 'src/components/BasicComponents/QRCode';
import { assembleTagGroupConfiguration } from 'src/components/Tags/assembleTagGroupConfiguration';
import { getRedeemKeyLink } from 'src/components/utils/helpers';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import { useEventFieldGenerator } from 'src/lib/customHooks/useEventFieldGenerator';
import { useIsChildAccount } from 'src/lib/customHooks/useIsChildAccount';
import { useBrandsOfEvent } from 'src/store/events/events_reducers';
import { useUser } from '../../../lib/customHooks';
import { useStore } from '../../../store/reducers';
import { ImageDropView } from '../../BasicComponents/ImageDropView';
import { BillingLink } from '../../BasicComponents/Links';
import { BlurFormTextarea } from '../../FormComponents';
import { TagGroupChooser } from '../../Tags';
import { getTagGroupsOfStarters } from '../../Tags';
import VisibilitySection from '../../VisibilitySection';
import { ChildEventsList } from './ChildEventsList';
import { GroupAddons } from './GroupAddons';
import { MetaDataPanel } from './MetaDataPanel/MetaDataPanel';

const { Title } = Typography;

// A cap, not the drawn size: the drop zone is capped at 100% of its column, so below this width it
// simply fills the column and stays square.
const LOGO_SIZE = 400;

// The field renders the textarea and its character counter in one div. Only the textarea should
// reach the bottom of the row, so the counter is lifted out of the flow and hung just below it —
// where the logo's fallback hint sits under its own box.
const DescriptionField = styled.div`
  position: relative;
  display: flex;
  flex: 1;

  > div {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  > div > span:last-of-type {
    position: absolute;
    top: 100%;
    font-size: 11px;
  }

  // Below antd's md breakpoint the columns stack, so there is no logo box to line up with and
  // nothing reserves the counter's line — it would sit on top of the next column.
  @media (max-width: 767px) {
    > div > span:last-of-type {
      position: static;
    }
  }
`;

function headingByType(type: EventTypes) {
  switch (type) {
    case EventTypes.CONTEST_GROUP: {
      return 'List of Contests';
    }
    case EventTypes.STAGE_GROUP: {
      return 'List of Stages';
    }
  }
  return 'Events';
}

function logoFallbackHint(type: EventTypes) {
  switch (type) {
    case EventTypes.CONTEST_GROUP: {
      return 'Contests without their own logo use this logo as a fallback.';
    }
    case EventTypes.STAGE_GROUP: {
      return 'Stages without their own logo use this logo as a fallback.';
    }
  }
  return 'Events without their own logo use this logo as a fallback.';
}

const WelcomeMessages = {
  REGULAR: 'Create your own Tracking Map',
  CONTEST: 'Create your own Tracking Contest',
  CONTEST_GROUP: 'Create a group of contests',
  STAGE: 'Create your own Tracking Stage',
  STAGE_GROUP: 'Create Your Tracking Map for Stages',
};

export const GroupBasicTab: FC = () => {
  const currentEvent = useCurrentEvent();
  const fieldGenerator = useEventFieldGenerator(currentEvent);
  const isCurrentUserChildAccount = useIsChildAccount();
  const brands = useBrandsOfEvent(currentEvent?.id);
  const { user } = useUser({ userId: currentEvent?.creatorId });
  const tagGroups = useMemo(() => getTagGroupsOfStarters(currentEvent?.starters), [currentEvent]);

  const { loadChildEvents } = useStore((s) => s.events.actions);

  useEffect(() => {
    if (currentEvent == null) return;
    if (isGroupEvent(currentEvent)) loadChildEvents(currentEvent.id);
  }, [currentEvent]);

  if (currentEvent == null || user == null || !isGroupEvent(currentEvent) || fieldGenerator == null)
    return null;

  const childEvents = currentEvent.childEvents || [];
  const { value: tagCategoriesConfig = {}, onChange: onTagCategoriesConfigChange } = fieldGenerator(
    'playerOptions.tagCategoriesConfig',
  );
  const selectedFilterTagKeys = tagGroups.filter(
    (tagKey) => tagCategoriesConfig[tagKey]?.visible ?? true,
  );

  const handleFilterTagKeysChange = (selectedTagKeys: Array<string>) => {
    const nextConfig = assembleTagGroupConfiguration(
      tagCategoriesConfig,
      tagGroups,
      selectedTagKeys,
    );
    onTagCategoriesConfigChange(nextConfig);
  };

  return (
    <Container>
      <section>
        <h4>{WelcomeMessages[currentEvent.type]}</h4>
        <Card>
          <div>
            Please fill out the basic information below to customize the Tracking Map for your
            Event. All settings and every information can be changed at any time.
          </div>
          {currentEvent.authorization === AuthorizationStates.NONE && (
            <div className="my-3">
              Trial: Test Live Tracking with Racemap App using the Passcode{' '}
              <strong>{currentEvent.passcode}</strong>.{' '}
              <a
                href="https://docs.racemap.com/apps/tracking/keys"
                target="_blank"
                rel="noopener noreferrer"
              >
                More about using the Passcode.
              </a>
            </div>
          )}

          <Row style={{ marginTop: '20px' }}>
            <Col md={24}>
              <QRCode
                isClickable={true}
                value={getRedeemKeyLink(currentEvent.passcode, brands[0])}
              />
              <small style={{ display: 'block', marginTop: '12px' }}>
                Participants may activate their Racemap App for your{' '}
                {currentEvent.type === EventTypes.STAGE_GROUP ? 'Stage' : 'Group'} Maps with this
                Passcode. Send the Passcode to your participants for them to sign up.{' '}
                <a
                  href="https://docs.racemap.com/apps/tracking/keys"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Read more about using the Passcode in Racemap App.
                </a>
              </small>
            </Col>
          </Row>

          <Row gutter={[16, 16]} align="stretch">
            <Col xs={24} md={5}>
              <Title level={5}>Logo</Title>
              {/* Square, and as wide as its column: the gap to the description is then the grid
                  gutter, the same gap the description has to the visibility column. */}
              <ImageDropView
                id="racemap_event_image_input"
                imageClass="logo"
                height={LOGO_SIZE}
                width={LOGO_SIZE}
                positionButton="inside-bottom-right"
                {...fieldGenerator('images')}
              />
            </Col>
            <Col xs={24} md={10} style={{ display: 'flex', flexDirection: 'column' }}>
              <Title level={5}>Description</Title>
              {/* Stretches to the logo box beside it, whatever width the logo scales to. */}
              <DescriptionField>
                <BlurFormTextarea
                  maxLength={250}
                  groupStyle={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                  style={{ flex: 1, resize: 'vertical' }}
                  id="racemap_event_description_input"
                  {...fieldGenerator('eventDescription')}
                />
              </DescriptionField>
            </Col>
            <Col xs={24} md={8}>
              <Title level={5}>Visibility</Title>
              <VisibilitySection
                authorized
                possibleStates={[VisibilityStates.UNLISTED, VisibilityStates.LISTED]}
                target={isContestGroupEvent(currentEvent) ? 'contestGroup' : 'stageGroup'}
                disabled={childEvents.length === 0}
                {...fieldGenerator('visibility')}
              />
            </Col>
          </Row>

          {/* Below the row, not inside the logo column: in there its height would set the row's,
              and the description would stretch past the logo box instead of ending level with it. */}
          <Row gutter={16}>
            <Col xs={24} md={5}>
              <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                {logoFallbackHint(currentEvent.type)}
              </Typography.Text>
            </Col>
          </Row>
        </Card>
      </section>
      <section>
        <MetaDataPanel />
      </section>
      <section>
        <h4 style={{ marginTop: '20px' }}>{headingByType(currentEvent.type)}</h4>
        <Card>
          <ChildEventsList />
        </Card>
      </section>
      <section>
        <h4>Participants</h4>
        <TagGroupChooser
          title="Filter Tags:"
          hint="Select from imported tags to set filter parameters for the participants list."
          groups={tagGroups}
          eventId={currentEvent.id}
          value={selectedFilterTagKeys}
          onChange={handleFilterTagKeysChange}
        />
      </section>
      <section>
        <GroupAddons />
      </section>
      {!isCurrentUserChildAccount && (
        <section>
          <h4>Pricing and Payment Information</h4>
          <Card>
            <BillingLink as="button" label="Open Your Billing Page" userId={user.id} openInNewTab />
          </Card>
        </section>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 40px;
  margin-left: auto;
  margin-right: auto;
  max-width: 1024px;

  section {
    width: 100%;
  }
`;
